// frontend/src/components/builder/DraggableItem.tsx
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CanvasItem } from '@/pages/BuilderPage';
import { GripVertical, X } from 'lucide-react';

interface DraggableItemProps {
  item: CanvasItem;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onUpdateProps?: (instanceId: string, newProps: any) => void;
  isPreviewMode?: boolean;
}

export default function DraggableItem({ item, isSelected, onSelect, onRemove, onUpdateProps, isPreviewMode }: DraggableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.instanceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Component = item.component;

  // In preview mode: render as a clean journey card (no drag handle, no remove button)
  if (isPreviewMode) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="mb-3 bg-white rounded-xl shadow-sm border border-gray-100 p-4"
      >
        <Component
          {...item.props}
          value={item.props}
          isBuilderMode={false}
          disabled={false}
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`mb-3 bg-white rounded-lg shadow-sm border flex flex-col overflow-hidden transition-shadow hover:shadow-md ${isSelected ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200'}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div className="p-1 bg-gray-50 border-b flex items-center gap-1 hover:bg-gray-100 group">
        {/* Drag Handle */}
        <div
          {...listeners}
          className="cursor-grab p-1.5 hover:bg-gray-200 rounded"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
        
        {/* Component Name */}
        <div className="flex-1 flex items-center text-xs font-semibold text-gray-600 px-1 py-1.5 truncate select-none">
          {item.metadata.name}
        </div>

        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove component"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="bg-white w-full overflow-hidden">
        <Component
          {...item.props}
          value={item.props}
          isBuilderMode={true}
          onChange={(newVal: any) =>
            onUpdateProps?.(item.instanceId, { ...item.props, ...newVal })
          }
        />
      </div>
    </div>
  );
}
